import * as THREE from 'three';
import type { InputManager } from '../../input';
import type { PlayerShip } from './PlayerShip';
import type { Terrain } from '../surface/Terrain';
import type { SurfaceObstacle } from '../surface/SurfaceObstacle';
import type { AudioManager } from '../../audio';
import type { PlayerStats } from '../player';

const SURFACE_FUEL_PER_SECOND = 0.06;

export interface SurfaceShipConfig {
  readonly acceleration: number;
  readonly maxSpeed: number;
  readonly liftAcceleration: number;
  readonly minGroundClearance: number;
  readonly damping: number;
  readonly mouseSensitivity: number;
  readonly maxPitch: number;
  readonly turnResponse: number;
  readonly maxRoll: number;
  readonly rollResponse: number;
  readonly bankAmount: number;
  readonly maxBank: number;
  readonly takeoffImpulse: number;
  readonly takeoffHeight: number;
  /** Soft circular boundary radius. 0 disables it. When the ship crosses
   *  this radius the controller clamps it back inside, zeros the radial
   *  velocity, and rotates the heading to face inward — no damage. */
  readonly boundaryRadius: number;
  /** Closing speed (u/s) above which a rock collision deals hull damage. */
  readonly collisionDamageThreshold: number;
  /** Damage = (closingSpeed - threshold) * scale, capped. */
  readonly collisionDamageScale: number;
  /** Cap on per-impact hull damage so a single bad hit can't insta-kill. */
  readonly collisionDamageCap: number;
}

export const DEFAULT_SURFACE_SHIP_CONFIG: SurfaceShipConfig = {
  acceleration: 30,
  maxSpeed: 52,
  liftAcceleration: 16,
  minGroundClearance: 1.4,
  damping: 0.988,
  mouseSensitivity: 0.002,
  maxPitch: Math.PI * 0.42,
  turnResponse: 5.8,
  maxRoll: 0.55,
  rollResponse: 3.6,
  bankAmount: 0.42,
  maxBank: 0.32,
  takeoffImpulse: 5.5,
  takeoffHeight: 4,
  boundaryRadius: 0,
  collisionDamageThreshold: 4,
  collisionDamageScale: 1.6,
  collisionDamageCap: 38
};

export class SurfaceShipController {
  private readonly forward = new THREE.Vector3();
  private readonly euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private targetYaw = 0;
  private targetPitch = 0;
  private yaw = 0;
  private pitch = 0;
  private roll = 0;
  private bankRoll = 0;
  private enabled = false;
  private isFlying = false;
  private takeoffTimer = 0;
  // Per-obstacle damage cooldown so a ship sitting against a rock doesn't
  // tick damage every frame; the player gets pushed off then armed again.
  private readonly hitCooldowns = new Map<SurfaceObstacle, number>();

  constructor(
    private readonly ship: PlayerShip,
    private readonly input: InputManager,
    private readonly terrain: Terrain,
    private readonly obstacles: readonly SurfaceObstacle[],
    private readonly config: SurfaceShipConfig = DEFAULT_SURFACE_SHIP_CONFIG,
    private readonly audio: AudioManager | null = null,
    private readonly stats: PlayerStats | null = null
  ) {}

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) {
      this.isFlying = false;
      this.takeoffTimer = 0;
      this.syncFromShip();
    } else {
      this.ship.setThrust(0);
      this.audio?.setEngineLevel(0);
    }
  }

  isInFlight(): boolean {
    return this.isFlying;
  }

  syncFromShip(): void {
    this.euler.setFromQuaternion(this.ship.object3D.quaternion, 'YXZ');
    this.yaw = this.euler.y;
    this.pitch = 0;
    this.targetYaw = this.yaw;
    this.targetPitch = 0;
    this.roll = 0;
    this.bankRoll = 0;
  }

  getAltitudeAboveGround(): number {
    const pos = this.ship.object3D.position;
    return pos.y - this.terrain.getCollisionGroundY(pos.x, pos.z);
  }

  update(dt: number): void {
    if (!this.enabled) return;

    if (!this.isFlying) {
      this.ship.velocity.set(0, 0, 0);
      this.ship.setThrust(0);
      this.audio?.setEngineLevel(0);

      if (this.input.isKeyDown('Space')) {
        this.isFlying = true;
        this.takeoffTimer = 0;
        this.ship.velocity.y = this.config.takeoffImpulse;
        this.audio?.playShipTakeoff();
      }
      return;
    }

    this.takeoffTimer += dt;
    const takeoffPhase = Math.min(this.takeoffTimer / 0.8, 1);

    if (this.input.isPointerLocked()) {
      const dx = this.input.getPointerDeltaX();
      const dy = this.input.getPointerDeltaY();
      this.targetYaw -= dx * this.config.mouseSensitivity;
      this.targetPitch -= dy * this.config.mouseSensitivity;
      this.targetPitch = THREE.MathUtils.clamp(
        this.targetPitch,
        -this.config.maxPitch,
        this.config.maxPitch
      );
    }

    const turnAlpha = 1 - Math.exp(-this.config.turnResponse * dt);
    this.yaw += (this.targetYaw - this.yaw) * turnAlpha;
    this.pitch += (this.targetPitch - this.pitch) * turnAlpha;

    const rollInput =
      (this.input.isKeyDown('KeyA') ? 1 : 0) -
      (this.input.isKeyDown('KeyD') ? 1 : 0);
    const targetRoll = rollInput * this.config.maxRoll;
    const rollAlpha = 1 - Math.exp(-this.config.rollResponse * dt);
    this.roll += (targetRoll - this.roll) * rollAlpha;

    const yawError = this.targetYaw - this.yaw;
    const bankTarget = THREE.MathUtils.clamp(
      -yawError * this.config.bankAmount,
      -this.config.maxBank,
      this.config.maxBank
    );
    const bankAlpha = 1 - Math.exp(-5 * dt);
    this.bankRoll += (bankTarget - this.bankRoll) * bankAlpha;

    let thrust =
      (this.input.isKeyDown('KeyW') ? 1 : 0) -
      (this.input.isKeyDown('KeyS') ? 1 : 0);
    let liftBoost = this.input.isKeyDown('Space') ? 1 : 0;
    const liftBrake = this.input.isKeyDown('ShiftLeft') ? 1 : 0;

    if (this.stats) {
      const demand = Math.abs(thrust) + liftBoost * 0.6;
      if (demand > 0) {
        if (this.stats.fuel <= 0) {
          thrust = 0;
          liftBoost = 0;
        } else {
          this.stats.consumeFuel(demand * SURFACE_FUEL_PER_SECOND * dt);
        }
      }
    }

    const body = this.ship.object3D;
    body.rotation.order = 'YXZ';
    this.euler.set(this.pitch, this.yaw, this.roll + this.bankRoll, 'YXZ');
    body.quaternion.setFromEuler(this.euler);

    this.forward.set(0, 0, -1).applyQuaternion(body.quaternion);
    const velocity = this.ship.velocity;
    const effectiveThrust = thrust * takeoffPhase;
    velocity.addScaledVector(
      this.forward,
      effectiveThrust * this.config.acceleration * dt
    );
    velocity.y +=
      (liftBoost - liftBrake) * this.config.liftAcceleration * dt * 0.7;

    const altNow = this.getAltitudeAboveGround();
    if (altNow < this.config.takeoffHeight && velocity.y < 1.2) {
      velocity.y += 1.5 * dt;
    }

    const damp = Math.pow(this.config.damping, dt * 60);
    velocity.x *= damp;
    velocity.z *= damp;
    velocity.y *= Math.pow(0.97, dt * 60);

    const horizontalSpeed = Math.sqrt(
      velocity.x * velocity.x + velocity.z * velocity.z
    );
    if (horizontalSpeed > this.config.maxSpeed) {
      const k = this.config.maxSpeed / horizontalSpeed;
      velocity.x *= k;
      velocity.z *= k;
    }

    body.position.addScaledVector(velocity, dt);

    const groundY = this.terrain.getCollisionGroundY(body.position.x, body.position.z);
    const minY = groundY + this.config.minGroundClearance;
    if (body.position.y < minY) {
      body.position.y = minY;
      if (velocity.y < 0) velocity.y = 0;
    }

    this.resolveObstacles(body.position, dt);
    this.enforceBoundary(body.position);

    const totalSpeed = Math.sqrt(
      velocity.x * velocity.x +
        velocity.y * velocity.y +
        velocity.z * velocity.z
    );
    const thrustLevel =
      thrust > 0 || liftBoost > 0
        ? Math.min(totalSpeed / this.config.maxSpeed + 0.2, 1)
        : 0;
    this.ship.setThrust(thrustLevel);
    this.audio?.setEngineLevel(Math.min(totalSpeed / this.config.maxSpeed, 1));
  }

  private resolveObstacles(position: THREE.Vector3, dt: number): void {
    const radius = this.ship.collisionRadius;
    const velocity = this.ship.velocity;
    // Ship-side margin: the ship's body extends ~1u below its origin, so
    // we treat the obstacle as "below us" only when the ship's belly is
    // higher than the obstacle's top.
    const SHIP_BELLY = 1.2;

    for (const [obstacle, cooldown] of this.hitCooldowns) {
      const next = cooldown - dt;
      if (next <= 0) this.hitCooldowns.delete(obstacle);
      else this.hitCooldowns.set(obstacle, next);
    }

    for (const obstacle of this.obstacles) {
      // Vertical overlap — if the ship is flying clearly above the
      // obstacle's top, skip horizontal pushback. Default height for
      // rock-sized obstacles is 5u, generous enough that low-altitude
      // strafing still hits rocks.
      const obstacleTop = obstacle.position.y + (obstacle.height ?? 5);
      if (position.y - SHIP_BELLY > obstacleTop) continue;

      const dx = position.x - obstacle.position.x;
      const dz = position.z - obstacle.position.z;
      const sum = radius + obstacle.radius;
      const distSq = dx * dx + dz * dz;
      if (distSq >= sum * sum) continue;

      const dist = Math.sqrt(Math.max(distSq, 1e-6));
      const push = sum - dist;
      const nx = dx / dist;
      const nz = dz / dist;
      position.x += nx * push;
      position.z += nz * push;

      const closing = -(velocity.x * nx + velocity.z * nz);
      if (closing > 0) {
        // Reflect with a soft bounce so the ship is shoved off rather than
        // pinned against the rock.
        const restitution = 0.45;
        velocity.x += closing * nx * (1 + restitution);
        velocity.z += closing * nz * (1 + restitution);

        if (
          this.stats &&
          closing > this.config.collisionDamageThreshold &&
          !this.hitCooldowns.has(obstacle)
        ) {
          const damage = Math.min(
            this.config.collisionDamageCap,
            (closing - this.config.collisionDamageThreshold) *
              this.config.collisionDamageScale
          );
          if (damage > 0) {
            this.stats.damageHull(damage);
            this.hitCooldowns.set(obstacle, COLLISION_REARM_SECONDS);
          }
        }
      }
    }
  }

  /** Soft circular boundary. The player is clamped just inside the radius
   *  with the radial velocity component zeroed, and the heading is reset
   *  to point back toward the origin — feels like the ship "thinks twice"
   *  rather than slamming into an invisible wall. */
  private enforceBoundary(position: THREE.Vector3): void {
    const r = this.config.boundaryRadius;
    if (r <= 0) return;
    const distSq = position.x * position.x + position.z * position.z;
    if (distSq <= r * r) return;

    const dist = Math.sqrt(distSq);
    const nx = position.x / dist;
    const nz = position.z / dist;
    // Clamp slightly inside so floating-point drift can't keep us re-firing.
    position.x = nx * (r - 0.5);
    position.z = nz * (r - 0.5);

    const velocity = this.ship.velocity;
    const radialV = velocity.x * nx + velocity.z * nz;
    if (radialV > 0) {
      velocity.x -= radialV * nx;
      velocity.z -= radialV * nz;
    }

    // Yaw to face origin: forward is -sin(yaw), -cos(yaw); we want
    // forward = (-nx, -nz), so yaw = atan2(nx, nz). Snap both yaw and
    // targetYaw so the smoothing doesn't fight the snap.
    const newYaw = Math.atan2(nx, nz);
    this.yaw = newYaw;
    this.targetYaw = newYaw;
  }
}

const COLLISION_REARM_SECONDS = 0.45;
